import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  CUSTOM_EMAIL_DOMAIN_TIER_KEYS,
  getMinimumTierKey,
  hasCustomEmailDomain,
  hasWhiteLabel,
  WHITE_LABEL_TIER_KEYS,
} from "@repo/types";
import {
  ActivationUsage,
  ActivationUsageRange,
  billingApi,
  UpcomingInvoice,
} from "services/billingApi";
import {
  CompanySubscription,
  subscriptionApi,
  SubscriptionPlan,
  VerifySubscriptionResponse,
} from "services/subscriptionApi";

const SUBSCRIPTION_PLANS_QUERY_KEY = ["subscription", "plans"];
const COMPANY_SUBSCRIPTION_QUERY_KEY = (companyId: string) => [
  "subscription",
  "company",
  companyId,
];
const UPCOMING_INVOICE_QUERY_KEY = (companyId: string) => [
  "billing",
  "upcoming-invoice",
  companyId,
];
const ACTIVATION_USAGE_QUERY_KEY = (
  companyId: string,
  range: ActivationUsageRange
) => ["billing", "activation-usage", companyId, range];

// `enabled` defaults to true so every existing caller is unchanged. It exists for
// callers that render app-wide (the sidebar) and must not fetch the catalog for a
// viewer who has no company plan.
export const useSubscriptionPlans = (enabled = true) =>
  useQuery<SubscriptionPlan[]>({
    queryKey: SUBSCRIPTION_PLANS_QUERY_KEY,
    queryFn: () => subscriptionApi.listPlans(),
    enabled,
    staleTime: 60_000,
  });

export const useCompanySubscription = (
  _companyId: string | null,
  enabled = true
) =>
  useQuery<CompanySubscription | null>({
    queryKey: COMPANY_SUBSCRIPTION_QUERY_KEY(_companyId ?? "current"),
    queryFn: () =>
      enabled ? subscriptionApi.getCurrentCompanySubscription() : null,
    enabled,
    staleTime: 30_000,
  });

export const useCreateSubscription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ priceId }: { priceId: string; companyId?: string }) =>
      billingApi.createSubscription(priceId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: SUBSCRIPTION_PLANS_QUERY_KEY,
      });
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          Array.isArray(queryKey) && queryKey[0] === "subscription",
      });
      queryClient.invalidateQueries({
        queryKey: COMPANY_SUBSCRIPTION_QUERY_KEY("current"),
      });
      if (variables.companyId) {
        queryClient.invalidateQueries({
          queryKey: COMPANY_SUBSCRIPTION_QUERY_KEY(variables.companyId),
        });
      }
    },
  });
};

export const useVerifySubscription = () => {
  const queryClient = useQueryClient();

  return useMutation<
    VerifySubscriptionResponse,
    unknown,
    { sessionId: string; companyId?: string }
  >({
    mutationFn: ({ sessionId }) =>
      subscriptionApi.verifySubscription(sessionId),
    onSuccess: (_, variables) => {
      if (variables.companyId) {
        queryClient.invalidateQueries({
          queryKey: COMPANY_SUBSCRIPTION_QUERY_KEY(variables.companyId),
        });
      } else {
        queryClient.invalidateQueries({
          predicate: ({ queryKey }) =>
            Array.isArray(queryKey) &&
            queryKey[0] === "subscription" &&
            queryKey[1] === "company",
        });
      }
      queryClient.invalidateQueries({
        queryKey: SUBSCRIPTION_PLANS_QUERY_KEY,
      });
    },
  });
};

const invalidateSubscriptionQueries = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({
    predicate: ({ queryKey }) =>
      Array.isArray(queryKey) && queryKey[0] === "subscription",
  });
  queryClient.invalidateQueries({
    queryKey: COMPANY_SUBSCRIPTION_QUERY_KEY("current"),
  });
};

export const useCancelSubscription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload?: { reason?: string; feedback?: string }) =>
      billingApi.cancelSubscription(payload),
    onSuccess: () => {
      invalidateSubscriptionQueries(queryClient);
    },
  });
};

export const useGrantFreeMonth = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => billingApi.grantFreeMonth(),
    onSuccess: () => {
      invalidateSubscriptionQueries(queryClient);
    },
  });
};

export const useUpcomingInvoice = (enabled = true) =>
  useQuery<UpcomingInvoice | null>({
    queryKey: UPCOMING_INVOICE_QUERY_KEY("current"),
    queryFn: () => (enabled ? billingApi.getUpcomingInvoice() : null),
    enabled,
    staleTime: 30_000,
  });

// VERA-646 / Slice B — the caller company's certificate-activation usage for the
// Billing Overview activations card.
//
// `range` is part of the query key, so each window is cached separately and
// switching back to one already seen is instant. `placeholderData` keeps the
// PREVIOUS range's response on screen while the next one loads, which matters
// because everything on this card except the chart (the headline count, the
// delta, the tier meter) is range-INDEPENDENT — dropping to `undefined` mid-switch
// would blank figures that are not changing. The card tells a stale chart from a
// fresh one by comparing `data.range` against the range it asked for.
export const useActivationUsage = (
  range: ActivationUsageRange = "month",
  enabled = true
) =>
  useQuery<ActivationUsage | null>({
    queryKey: ACTIVATION_USAGE_QUERY_KEY("current", range),
    queryFn: () => (enabled ? billingApi.getActivationUsage(range) : null),
    enabled,
    staleTime: 30_000,
    placeholderData: (previousData) => previousData,
  });

/**
 * Whether the current company's plan entitles it to white-label (hide the
 * "Powered by Vera" attribution). **Top tiers only** — Scale and the
 * contact-sales Enterprise, per `WHITE_LABEL_TIER_KEYS` — the top-tier perk
 * (see context/decisions/0004-plan-packaging.md). The gate is REAL; the toggle's
 * effect (actually hiding the badge on cert/email) + persistence land with TD-066
 * (billing entitlements). Shared by the canonical Features toggle and the Studio
 * satellite card so the entitlement has one source of truth.
 */
export const useCanWhiteLabel = (): {
  canWhiteLabel: boolean;
  /** The cheapest plan's Stripe-authored title that unlocks white-label — e.g.
   *  "Scale". For an "Upgrade to {title}" CTA; undefined while plans are loading
   *  or if no synced plan carries the minimum tier key (e.g. `enterprise`, which
   *  has no Stripe price to sync — see `PLAN_TIER_KEYS`'s own comment). */
  minimumPlanTitle: string | undefined;
  isLoading: boolean;
} => {
  const { data: plans = [], isLoading: plansLoading } = useSubscriptionPlans();
  const { data: subscription, isLoading: subLoading } =
    useCompanySubscription(null);
  const currentPlan =
    subscription && subscription.status === "active"
      ? plans.find((p) => p.id === subscription.stripe_product_id) ?? null
      : null;
  const minimumTierKey = getMinimumTierKey(WHITE_LABEL_TIER_KEYS);
  return {
    // Keyed on tier_key, NOT title. This read `title === "enterprise"` until the
    // Stripe product was renamed to "Vera Scale" (VRG-393), at which point it
    // silently returned false for every customer who had paid for white-label —
    // no error, no failing test, the capability simply switched off. Same defect
    // VRG-365 fixed in the comparison matrix. `hasWhiteLabel` is shared with the
    // two API-side gates so the three cannot drift apart again.
    canWhiteLabel: hasWhiteLabel(currentPlan?.tier_key),
    minimumPlanTitle: plans.find((p) => p.tier_key === minimumTierKey)?.title,
    isLoading: plansLoading || subLoading,
  };
};

/**
 * VERA-362 — whether the current company's plan entitles it to a custom email
 * sending domain. Business plan or higher (`CUSTOM_EMAIL_DOMAIN_TIER_KEYS`) —
 * a WIDER set than `useCanWhiteLabel`'s (that one is Scale+/Enterprise only).
 * Mirrors `useCanWhiteLabel`'s tier_key-not-title lookup so the two entitlements
 * can never drift apart the way the VRG-393 rename bug did.
 */
export const useCanAddEmailDomain = (): {
  canAddEmailDomain: boolean;
  /** See `useCanWhiteLabel`'s `minimumPlanTitle` doc — same idea, this
   *  entitlement's own minimum tier ("Business"). */
  minimumPlanTitle: string | undefined;
  isLoading: boolean;
} => {
  const { data: plans = [], isLoading: plansLoading } = useSubscriptionPlans();
  const { data: subscription, isLoading: subLoading } =
    useCompanySubscription(null);
  const currentPlan =
    subscription && subscription.status === "active"
      ? plans.find((p) => p.id === subscription.stripe_product_id) ?? null
      : null;
  const minimumTierKey = getMinimumTierKey(CUSTOM_EMAIL_DOMAIN_TIER_KEYS);
  return {
    canAddEmailDomain: hasCustomEmailDomain(currentPlan?.tier_key),
    minimumPlanTitle: plans.find((p) => p.tier_key === minimumTierKey)?.title,
    isLoading: plansLoading || subLoading,
  };
};

export type {
  SubscriptionPlan,
  CompanySubscription,
} from "services/subscriptionApi";
